import { ReactNode } from 'react';

import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { PresentationBlockHoverRegion } from '@/modules/presentation/components/PresentationHoverActions/PresentationBlockHoverRegion';

import { ClientPresentationDesignerV2_EntityFragment } from '../../../../graphql/ClientPresentationDesignerV2.generated';
import { PresentationEntityActionTarget } from '../../../presentationEntityActions/presentationEntityActions.context';
import {
  EntityCoverGrayBlockToolbar,
  EntityCoverHeaderToolbar,
} from './EntityCoverSlideToolbars';

/** Wraps a cover-slide section with its edit-mode hover toolbar. */
type SectionRenderer = (section: ReactNode) => ReactNode;

interface UseEntityCoverToolbarsParams {
  entity: ClientPresentationDesignerV2_EntityFragment;
  /** Base id for the hover regions; each region gets a distinct suffix. */
  slideId: string;
  /**
   * When false (e.g. a view-only viewer), no render-props are returned and the
   * cover sections render plainly.
   */
  canEdit?: boolean;
}

/**
 * Builds the cover slide's hover-toolbar render-props from a plain entity + a
 * slide id, so any surface (the presentation designer or the single-entity
 * export preview) can add the same "Edit entity"/"Edit holdings" header toolbar
 * and the gray-block "Edit <section>" toolbar without the designer-only slide
 * registration. Returns empty when editing is off.
 */
export function useEntityCoverToolbars({
  entity,
  slideId,
  canEdit = true,
}: UseEntityCoverToolbarsParams): {
  renderHeaderSection?: SectionRenderer;
  renderDetailsSection?: SectionRenderer;
} {
  if (!canEdit) {
    return {};
  }

  const target: PresentationEntityActionTarget = {
    entityId: entity.id,
    entityType: getEntityTypeFromEntityKind(entity.kind),
  };

  // Distinct ids so only one of the two regions shows its toolbar at a time.
  const renderHeaderSection: SectionRenderer = (section) => (
    <PresentationBlockHoverRegion
      id={`${slideId}-header`}
      renderToolbar={({ dismiss }) => (
        <EntityCoverHeaderToolbar
          target={target}
          entity={entity}
          onDismiss={dismiss}
        />
      )}
    >
      {section}
    </PresentationBlockHoverRegion>
  );

  const renderDetailsSection: SectionRenderer = (section) => (
    <PresentationBlockHoverRegion
      id={`${slideId}-details`}
      renderToolbar={({ dismiss }) => (
        <EntityCoverGrayBlockToolbar
          target={target}
          entityKind={entity.kind}
          onDismiss={dismiss}
        />
      )}
    >
      {section}
    </PresentationBlockHoverRegion>
  );

  return { renderHeaderSection, renderDetailsSection };
}
